
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { 
  generateCsrfToken, 
  storeCsrfToken, 
  verifyCsrfToken, 
  checkRateLimit,
  sanitizeInput
} from "@/utils/security";

interface SharedClosetAccessProps {
  onAccessGranted?: () => void;
  redirectPath?: string;
}

const SharedClosetAccess = ({ onAccessGranted, redirectPath }: SharedClosetAccessProps) => {
  const [username, setUsername] = useState("");
  const [pagePassword, setPagePassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { accessSharedCloset } = useAuth();
  const navigate = useNavigate();
  
  // Security enhancement: CSRF token
  const [csrfToken, setCsrfToken] = useState("");
  
  useEffect(() => {
    // Generate a new CSRF token on component mount
    const token = generateCsrfToken();
    setCsrfToken(token);
    storeCsrfToken("sharedAccessCsrfToken", token);
  }, []);

  // Security enhancement: Input validation
  const validateInputs = () => {
    if (!username) {
      setError("Username is required");
      return false;
    }
    
    if (!/^[a-zA-Z0-9_.-]+$/.test(username)) {
      setError("Username contains invalid characters");
      return false;
    }
    
    if (!pagePassword) {
      setError("Password is required");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    // Security enhancement: Verify CSRF token
    if (!verifyCsrfToken("sharedAccessCsrfToken", csrfToken)) {
      setError("Security verification failed. Please refresh the page and try again.");
      return;
    }
    
    // Validate inputs before submission
    if (!validateInputs()) {
      return;
    }
    
    // Check rate limiting
    const rateLimit = checkRateLimit("shared_access_attempt");
    if (!rateLimit.allowed) {
      setError(`Too many access attempts. Please try again in ${rateLimit.remainingTime} seconds.`);
      return;
    }
    
    setIsSubmitting(true);

    try {
      const sanitizedUsername = sanitizeInput(username);
      const success = await accessSharedCloset(sanitizedUsername, pagePassword);
      
      if (success) {
        if (onAccessGranted) {
          onAccessGranted();
        }
        if (redirectPath) {
          navigate(redirectPath);
        }
      } else {
        // If accessSharedCloset returns false but doesn't throw,
        // show a generic error (the specific error would be shown via toast)
        setError("Could not access this closet. Please check your credentials.");
      }
    } catch (error: any) {
      setError(error.message || "An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
      
      // Generate a new CSRF token after submission
      const newToken = generateCsrfToken();
      setCsrfToken(newToken);
      storeCsrfToken("sharedAccessCsrfToken", newToken);
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-xl">Access Shared Closet</CardTitle>
        <CardDescription>
          Enter the username and page password to view someone's closet
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* CSRF Token (hidden) */}
          <input type="hidden" name="_csrf" value={csrfToken} />
          
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter username"
              required
              pattern="[a-zA-Z0-9_.-]+"
              title="Username can only contain letters, numbers, underscores, dots and hyphens"
              maxLength={30}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pagePassword">Page Password</Label>
            <Input
              id="pagePassword"
              type="password"
              value={pagePassword}
              onChange={(e) => setPagePassword(e.target.value)}
              placeholder="Enter page password"
              required
              maxLength={50}
            />
          </div>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={isSubmitting}
          >
            {isSubmitting ? "Checking..." : "Access Closet"}
          </Button>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center text-sm text-gray-500">
        <p>This allows view-only access to someone else's closet</p>
      </CardFooter>
    </Card>
  );
};

export default SharedClosetAccess;
